// ============================================================================
// Brand wallet — client-side view + split preview
// ============================================================================
// The wallet holds credit from three sources: refunded for hires a creator
// never delivered, granted by BMC, or BOUGHT up front by the brand. Whichever
// way it arrived it spends identically — automatically on the next hire, credit
// first, card for whatever's left.
//
// planHirePayment here is a MIRROR of worker/src/wallet.ts. The worker computes
// the real split at charge time against the live balance; this exists so the
// brand sees the same arithmetic before they send an offer. Keep the two in
// sync — including MIN_CARD_CHARGE_CENTS, which is the reason the split isn't
// always "use every available cent".
// ============================================================================

/** Whop declines charges under its floor, so we never leave a smaller remainder
 *  on the card. Mirror of the worker constant. */
export const MIN_CARD_CHARGE_CENTS = 100

export interface WalletPlan {
  totalCents: number
  creditCents: number
  cardCents: number
  /**
   * 'credit_covers_all' — the wallet pays for the whole hire, card untouched.
   * 'card_floor'        — credit was held back so the card charge clears Whop's
   *                       minimum (the leftover would otherwise be declined).
   */
  adjustment: 'none' | 'credit_covers_all' | 'card_floor'
}

export function planHirePayment(totalCents: number, availableCreditCents: number): WalletPlan {
  const total = Math.max(0, Math.round(totalCents))
  const available = Math.max(0, Math.round(availableCreditCents))

  if (available <= 0 || total <= 0) {
    return { totalCents: total, creditCents: 0, cardCents: total, adjustment: 'none' }
  }
  if (available >= total) {
    return { totalCents: total, creditCents: total, cardCents: 0, adjustment: 'credit_covers_all' }
  }

  let credit = available
  let card = total - credit

  if (card < MIN_CARD_CHARGE_CENTS) {
    if (total <= MIN_CARD_CHARGE_CENTS) {
      return { totalCents: total, creditCents: credit, cardCents: card, adjustment: 'none' }
    }
    credit = total - MIN_CARD_CHARGE_CENTS
    card = MIN_CARD_CHARGE_CENTS
    return { totalCents: total, creditCents: credit, cardCents: card, adjustment: 'card_floor' }
  }

  return { totalCents: total, creditCents: credit, cardCents: card, adjustment: 'none' }
}

/** Spendable credit from a brand row. Reserved credit (an accepted hire whose
 *  charge is still settling) is already committed elsewhere and must not be
 *  offered twice. */
export function availableCredit(
  brand: { account_credit_cents?: number; credit_held_cents?: number } | null | undefined
): number {
  const balance = brand?.account_credit_cents ?? 0
  const held = brand?.credit_held_cents ?? 0
  return Math.max(0, balance - held)
}

// ── Balance freshness ───────────────────────────────────────────────────────
// The sidebar badge reads the balance off the shared brand context, which loads
// once per session and refreshes on window focus. That covers credit that moved
// elsewhere (a creator accepting an offer, an admin refund) but NOT credit the
// brand just spent in this tab: hiring never changes focus, so the badge kept
// showing the pre-hire balance until a navigation happened to reload the row.
//
// Any action that moves credit calls notifyWalletChanged() once the worker has
// confirmed it. Listeners refetch from their own source of truth rather than
// having the amount pushed at them — the worker owns the arithmetic (holds,
// card floor, concurrent hires), and a locally-decremented number would drift
// the moment any of that applied.

export const WALLET_CHANGED_EVENT = 'bmc:wallet-changed'

/** Announce that this brand's credit balance has moved. Safe to call more than
 *  once per action — listeners refetch, so a duplicate is a wasted read, not a
 *  wrong number. */
export function notifyWalletChanged(): void {
  window.dispatchEvent(new Event(WALLET_CHANGED_EVENT))
}

/** Subscribe to balance changes. Returns the unsubscribe function, for direct
 *  use as a useEffect cleanup. */
export function onWalletChanged(handler: () => void): () => void {
  window.addEventListener(WALLET_CHANGED_EVENT, handler)
  return () => window.removeEventListener(WALLET_CHANGED_EVENT, handler)
}

// ── Which rail a payment came down, and how long to promise ─────────────────
//
// Every "we're waiting on this payment" screen needs this, and getting it wrong
// is expensive in both directions: telling an ACH payer their credit is seconds
// away, or telling a card payer to wait 3-5 business days.
//
// THE HARD PART IS THAT THE RAIL IS USUALLY UNKNOWN WHILE WE ARE WAITING. Whop
// does not expose a payment method until the payment settles, so a top-up in
// flight almost always has payment_method null — which is precisely when this
// copy is on screen. So `unknown` is the common case, not the edge case, and it
// must be honest for a card AND a bank transfer at the same time. It names both
// timings rather than picking one, and promises an email instead of a deadline.
//
// A wire is kept separate from ACH deliberately. It clears faster and, unlike an
// ACH debit, cannot be returned weeks later.

export type PaymentRail = 'card' | 'ach' | 'wire' | 'crypto' | 'unknown'

/** Normalise what the worker recorded (see describePaymentMethod in
 *  worker/src/whop.ts, which does the same mapping on Whop's raw value). */
export function railOf(paymentMethod: string | null | undefined): PaymentRail {
  const v = (paymentMethod ?? '').toLowerCase().trim()
  if (!v) return 'unknown'
  if (/card|credit|debit/.test(v)) return 'card'
  // Before the bank test: Whop may report a wire as 'bank_wire'.
  if (/wire/.test(v)) return 'wire'
  if (/ach|bank|us_bank/.test(v)) return 'ach'
  if (/crypto|coin|usdc|eth|btc/.test(v)) return 'crypto'
  return 'unknown'
}

export interface RailWaitCopy {
  /** Short status label, e.g. next to an amount. */
  label: string
  /** One or two sentences explaining the wait. */
  detail: string
  /** True when this rail genuinely takes days, so a caller can pick a
   *  bank-ish icon or suppress a spinner that would imply seconds. */
  isBankTransfer: boolean
}

/**
 * How long a payment may be "in progress" before we stop calling it that.
 *
 * Mirrors TOPUP_PROCESSING_EXPECTED_DAYS in worker/src/creditTopup.ts, which
 * logs the same threshold. Seven days clears the slowest rail on offer with room
 * to spare — ACH is 3-5 BUSINESS days, a wire about one.
 *
 * Past it, waitingCopyFor's reassurance is no longer something we know to be
 * true. A $10,000 wire sat for three days showing "Your credit appears the
 * moment the money arrives" while Whop's own dashboard showed the payment as
 * Incomplete, and nothing on the brand's screen hinted at the difference.
 */
export const TOPUP_PROCESSING_EXPECTED_DAYS = 7

export function topupAgeDays(createdAt: string, now = Date.now()): number {
  return (now - new Date(createdAt).getTime()) / 86_400_000
}

export function topupIsOverdue(createdAt: string, now = Date.now()): boolean {
  return topupAgeDays(createdAt, now) > TOPUP_PROCESSING_EXPECTED_DAYS
}

/**
 * Copy for a payment that has been in flight longer than any rail takes.
 *
 * Says what is known and stops there. We cannot tell a wire still in transit
 * from one the brand never sent — Whop cannot either, which is exactly why its
 * status says "incomplete" rather than "failed" — so this names the wire case
 * without asserting it, and offers the one action that always helps.
 *
 * Takes the rail so a KNOWN card or ACH does not get the wire sentence. The rail
 * is usually unknown here, which is the case the default covers.
 */
export function overdueCopyFor(rail: PaymentRail, ageDays: number): RailWaitCopy {
  const days = Math.max(1, Math.floor(ageDays))
  const wire = rail === 'wire' || rail === 'unknown'
    ? ' A wire only completes once your bank actually sends it, so if that is how you paid, check that it went out.'
    : ''
  return {
    label: 'Not received yet',
    detail:
      `This payment was started ${days} days ago and still has not reached us.${wire}` +
      ' No credit has been added and nothing has been charged by us — contact support and we will chase it with the payment provider.',
    isBankTransfer: rail === 'ach' || rail === 'wire',
  }
}

export function waitingCopyFor(rail: PaymentRail): RailWaitCopy {
  switch (rail) {
    case 'ach':
      return {
        label: 'Bank transfer clearing',
        detail: 'Bank transfers take 3–5 business days. Your credit appears as soon as the money arrives, and we\'ll email you the moment it does.',
        isBankTransfer: true,
      }
    case 'wire':
      return {
        label: 'Wire transfer clearing',
        detail: 'Wire transfers usually arrive within one business day. Your credit appears as soon as the money does, and we\'ll email you.',
        isBankTransfer: true,
      }
    case 'card':
      return {
        label: 'Payment processing',
        detail: 'We\'re confirming this payment with your card issuer. Your credit appears as soon as it clears.',
        isBankTransfer: false,
      }
    default:
      // Says both timings because we genuinely do not know which this is, and
      // sets no deadline it cannot keep — the email is the promise.
      return {
        label: 'Payment processing',
        detail: 'We\'re waiting for this payment to clear. Card payments usually take seconds; bank transfers take 3–5 business days. Your credit appears the moment the money arrives, and we\'ll email you then.',
        isBankTransfer: false,
      }
  }
}

// ── Ledger presentation ─────────────────────────────────────────────────────

export type LedgerStatus = 'hold' | 'posted' | 'released' | 'reversed'

export interface WalletLedgerEntry {
  id: string
  amount_cents: number
  reason: string
  status: LedgerStatus
  related_hire_id: string | null
  notes: string | null
  created_by: string | null
  created_at: string
  posted_at: string | null
  released_at: string | null
  balance_after_cents: number | null
  metadata: Record<string, unknown> | null
}

/**
 * A credit purchase that has NOT produced a ledger entry — still clearing, or
 * failed. Separate from WalletLedgerEntry because no money has moved: there is
 * no balance_after and no signed amount, and rendering one as a ledger row would
 * claim otherwise.
 *
 * Exists because the brand's history was success-only. Someone who authorised a
 * bank transfer days ago, or whose card was declined, saw nothing at all — the
 * ledger records movements, and neither of those is one yet.
 */
export interface WalletPendingTopup {
  id: string
  status: 'processing' | 'failed'
  expected_amount_cents: number
  /** Only known once a payment settles, so usually null while processing. */
  payment_method: string | null
  error_message: string | null
  /** What Whop said the last time the worker asked. Null on rows that predate
   *  the column, or that nothing has re-checked yet. Shown, never acted on. */
  last_whop_status?: string | null
  last_whop_substatus?: string | null
  created_at: string
}

export interface WalletSummary {
  brand_id: string
  balance_cents: number
  held_cents: number
  available_cents: number
  entries: WalletLedgerEntry[]
  pending_topups?: WalletPendingTopup[]
  plan?: {
    total_cents: number
    credit_cents: number
    card_cents: number
    adjustment: WalletPlan['adjustment']
  } | null
}

/** Brand-facing label for a ledger reason. Deliberately plain — a brand
 *  shouldn't have to decode internal reason codes to understand their money. */
export function ledgerReasonLabel(reason: string): string {
  switch (reason) {
    case 'hire_refund':         return 'Refund — creator missed deadline'
    case 'failed_hire':         return 'Refund — hire failed'
    case 'hire_release_refund': return 'Refund — hire released'
    case 'applied_to_hire':     return 'Applied to a hire'
    // An admin added credit by hand (goodwill, onboarding, a make-good). The
    // admin's reason is mandatory and lands in entry.notes, which the ledger row
    // renders underneath — so this label only has to say where it came from.
    case 'admin_grant':         return 'Credit added by BMC'
    // An admin removed credit by hand. Same admin-attributed pattern as
    // admin_grant, opposite direction — entry.notes carries the admin's
    // mandatory reason, rendered underneath by the ledger row.
    case 'admin_deduct':        return 'Credit deducted by BMC'
    // The brand bought this credit. "Purchased" rather than "Added" so their
    // own payment is distinguishable at a glance from credit BMC handed them —
    // they reconcile these two against different things (a card statement vs a
    // support conversation).
    case 'credit_topup':          return 'Credit purchased'
    // That purchase was refunded or charged back, so the credit came out again.
    case 'credit_topup_reversal': return 'Credit purchase refunded'
    case 'manual_adjust':       return 'Adjustment'
    case 'refund_on_cancel':    return 'Refund — cancelled'
    case 'reversal':            return 'Correction'
    default:                    return reason.replace(/_/g, ' ')
  }
}

/** How a ledger row should read: money in, money out, or not settled yet. */
export function ledgerDirection(entry: WalletLedgerEntry): 'in' | 'out' | 'pending' | 'void' {
  if (entry.status === 'hold') return 'pending'
  if (entry.status === 'released' || entry.status === 'reversed') return 'void'
  return entry.amount_cents >= 0 ? 'in' : 'out'
}
